import { without } from 'lodash';
import { expandPricetypeQuantities } from '../../events/detail/TicketSelectorUtils';
import { SyosSelectionMode } from '../SyosConfig';
import { IPriceTypeSelectionHelper, IPriceTypeId } from './types';

class FlexPriceTypeSelectionHelper implements IPriceTypeSelectionHelper {
  mode: SyosSelectionMode = 'flex';

  private expandedPricetypeQuantities: { pricetypeId: number; quantity: number; }[];

  #nextPriceTypeId?: number;

  #isSelectionComplete = false;

  #selectionCount = 0;

  #totalSelectionCount = 0;

  get isSelectionComplete() { return this.#isSelectionComplete; }

  get selectionCount() { return this.#selectionCount; }

  get totalSelectionCount() { return this.#totalSelectionCount; }

  get selectionsRemaining() { return this.#totalSelectionCount - this.#selectionCount; }

  get canMakeAnotherSelection() {
    if (this.isSelectionComplete) {
      return false;
    }

    return true;
  }

  constructor(private priceTypeQuantities: number[]) {
    if (!priceTypeQuantities.length) {
      throw new Error('Invalid empty priceTypeQuantities');
    }

    this.expandedPricetypeQuantities = expandPricetypeQuantities(this.priceTypeQuantities);

    this.#nextPriceTypeId = this.calculateNextPriceTypeId([]);

    this.#totalSelectionCount = priceTypeQuantities.length;
  }

  getFilteredPriceTypes<T extends IPriceTypeId>(pricetypes: T[]): T[] {
    const nextPriceTypeId = this.#nextPriceTypeId;

    if (nextPriceTypeId === undefined) {
      return [];
    }

    return pricetypes.filter((p) => p.price_type === nextPriceTypeId);
  }

  public updateSelections(selections: number[]) {
    this.#nextPriceTypeId = this.calculateNextPriceTypeId(selections);

    this.#isSelectionComplete = selections.length === this.priceTypeQuantities.length;

    this.#selectionCount = selections.length;
  }

  private calculateNextPriceTypeId(selectedPriceTypeIds: number[]) {
    if (selectedPriceTypeIds.length === 0) {
      return this.priceTypeQuantities[0];
    }

    const unknowns = without(selectedPriceTypeIds, ...this.priceTypeQuantities);

    if (unknowns.length) {
      throw new Error('Unknown pricetype selections');
    }

    const selectedQuantitiesMap = makeQuantityMap(selectedPriceTypeIds.map((s) => s));

    for (let index = 0; index < this.expandedPricetypeQuantities.length; index += 1) {
      const { pricetypeId, quantity } = this.expandedPricetypeQuantities[index];

      const selected = selectedQuantitiesMap.get(pricetypeId);

      if (!selected) {
        return pricetypeId;
      }

      if (selected > quantity) {
        throw new Error('Too many selections for pricetype');
      }

      if (selected < quantity) {
        return pricetypeId;
      }
    }

    return undefined;
  }
}

function makeQuantityMap(ids: number[]) {
  const map = new Map<number, number>();

  ids.forEach((id) => {
    const total = map.get(id) || 0;
    map.set(id, total + 1);
  });

  return map;
}

export default FlexPriceTypeSelectionHelper;
